import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TrendingUp, Award, Target, Calendar, CheckCircle, Flame, 
  BarChart3, Activity, Sparkles, Clock, Trophy, Star
} from "lucide-react";

const ProgressDashboard = () => {
  const { user, token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("7days");
  const [progressData, setProgressData] = useState(null);
  const [readinessData, setReadinessData] = useState(null);

  useEffect(() => {
    if (user && token) {
      fetchProgressData();
      fetchReadinessScore();
    }
  }, [user, token, period]);

  const fetchProgressData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/progress/dashboard?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProgressData(response.data);
    } catch (error) {
      toast.error("Failed to load progress data");
    } finally {
      setLoading(false);
    }
  };

  const fetchReadinessScore = async () => {
    try {
      const response = await axios.get(`${API}/progress/career-readiness`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReadinessData(response.data);
    } catch (error) {
      toast.error("Failed to load readiness score");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="w-full">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-2">Your Progress Dashboard</h1>
            <p className="text-gray-600">Track your career readiness journey</p>
          </div>

          {/* Career Readiness Score - Prominent Card */}
          {readinessData && (
            <Card className="mb-6 md:mb-8 border-2 bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
              <CardContent className="pt-6 pb-6">
                <div className="grid md:grid-cols-3 gap-4 md:gap-6">
                  {/* Score Circle */}
                  <div className="flex flex-col items-center justify-center">
                    <div className="relative w-32 h-32 md:w-40 md:h-40">
                      <svg className="transform -rotate-90 w-32 h-32 md:w-40 md:h-40">
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="currentColor"
                          strokeWidth="10"
                          fill="none"
                          className="text-white opacity-20"
                        />
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="currentColor"
                          strokeWidth="10"
                          fill="none"
                          strokeDasharray={`${2 * Math.PI * 56}`}
                          strokeDashoffset={`${2 * Math.PI * 56 * (1 - readinessData.readiness_score / 100)}`}
                          className="text-white transition-all duration-1000"
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="text-3xl md:text-4xl font-bold">{readinessData.readiness_score}%</div>
                        <div className="text-xs md:text-sm opacity-90">Readiness</div>
                      </div>
                    </div>
                    <Badge className="mt-3 md:mt-4 bg-white text-indigo-600 text-sm md:text-lg py-1 px-3 md:px-4">
                      {readinessData.level}
                    </Badge>
                  </div>

                  {/* Message and Breakdown */}
                  <div className="md:col-span-2">
                    <div className="flex items-center gap-2 mb-3 md:mb-4">
                      <Trophy className="w-5 h-5 md:w-6 md:h-6" />
                      <h3 className="text-xl md:text-2xl font-bold">Career Readiness Score</h3>
                    </div>
                    <p className="text-base md:text-lg mb-4 md:mb-6 opacity-90">{readinessData.message}</p>

                    {/* Breakdown */}
                    <div className="space-y-2 md:space-y-3">
                      {Object.entries(readinessData.breakdown).map(([key, data]) => (
                        <div key={key}>
                          <div className="flex justify-between text-xs md:text-sm mb-1 opacity-90">
                            <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                            <span>{data.score}% (Weight: {data.weight}%)</span>
                          </div>
                          <Progress value={data.score} className="h-2 bg-white/20" />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Time Period Selector */}
          <div className="mb-6 flex gap-2">
            <Button
              variant={period === "7days" ? "default" : "outline"}
              onClick={() => setPeriod("7days")}
            >
              Last 7 Days
            </Button>
            <Button
              variant={period === "30days" ? "default" : "outline"}
              onClick={() => setPeriod("30days")}
            >
              Last 30 Days
            </Button>
          </div>

          {/* Stats Grid */}
          {progressData && (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Assessments */}
              <Card className="border-l-4 border-l-blue-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                    Assessments
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{progressData.assessments_completed}</div>
                  <p className="text-sm text-gray-600 mt-1">Completed</p>
                </CardContent>
              </Card>

              {/* Consultations */}
              <Card className="border-l-4 border-l-green-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Activity className="w-5 h-5 text-green-500" />
                    Consultations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{progressData.consultations_taken}</div>
                  <p className="text-sm text-gray-600 mt-1">Attended</p>
                </CardContent>
              </Card>

              {/* Quizzes */}
              <Card className="border-l-4 border-l-purple-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Target className="w-5 h-5 text-purple-500" />
                    Quizzes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{progressData.quizzes_attempted}</div>
                  <p className="text-sm text-gray-600 mt-1">Attempted</p>
                </CardContent>
              </Card>

              {/* Points */}
              <Card className="border-l-4 border-l-orange-500">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Sparkles className="w-5 h-5 text-orange-500" />
                    Points
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{progressData.total_points}</div>
                  <p className="text-sm text-gray-600 mt-1">Total Earned</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Login Streak & Badges */}
          {progressData && (
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Login Streak */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Flame className="w-5 h-5 text-orange-500" />
                    Login Streak
                  </CardTitle>
                  <CardDescription>Keep your streak alive!</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="text-5xl font-bold text-orange-500">
                      {progressData.login_streak}
                    </div>
                    <div>
                      <p className="text-lg font-semibold">Days in a row</p>
                      <p className="text-sm text-gray-600">Come back daily to maintain your streak</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Badges */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5 text-yellow-500" />
                    Badges Earned
                  </CardTitle>
                  <CardDescription>Your achievements</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold mb-3">{progressData.badges_count} Badges</div>
                  {progressData.badges.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {progressData.badges.slice(0, 6).map((badge, idx) => (
                        <Badge key={idx} variant="secondary">
                          {badge}
                        </Badge>
                      ))}
                      {progressData.badges.length > 6 && (
                        <Badge variant="outline">+{progressData.badges.length - 6} more</Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">Complete activities to earn badges!</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Activity Timeline */}
          {progressData && progressData.timeline && progressData.timeline.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-indigo-500" />
                  Recent Activity
                </CardTitle>
                <CardDescription>Your latest achievements and milestones</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {progressData.timeline.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl">{activity.icon}</div>
                      <div className="flex-1">
                        <p className="font-semibold">{activity.title}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(activity.timestamp).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ProgressDashboard;
